import { getService } from "../../reducers/service";
import { serviceNames } from "../../utils/constants";

export const getFundingSourceData = async (id) => {
	const service = await getService(serviceNames.perFundingSources);
	return service.find({
		query: {
			id,
			isDeleted: false,
		},
	});
};

export const getFundingSourceOptions = async (userAgency) => {
	const service = await getService(serviceNames.perFundingSourceOptions);
	const response = await service.find({
		query: {
			agencyId: userAgency,
			isDeleted: false,
		},
	});

	return response?.map((value) => ({
		label: value,
		value,
	}));
};

export const getFundingAdministratorOptions = async (userAgency) => {
	const service = await getService(
		serviceNames.perFundingAdministratorOptions,
	);
	const response = await service.find({
		query: {
			agencyId: userAgency,
			isDeleted: false,
		},
	});

	return response?.map((value) => ({
		label: value,
		value,
	}));
};

// LINKED RECORDS - Fetch data by Funding Source ID

/**
 * Fetch vehicles linked to a funding source
 */
export const getLinkedVehicles = async (fundingSourceId, userAgency) => {
	try {
		const service = await getService(serviceNames.perVehicles);
		const response = await service.find({
			query: {
				fundingSourceId: fundingSourceId,
				agencyId: userAgency,
				isDeleted: false,
				$limit: -1,
			},
		});
		return response?.data || response || [];
	} catch (error) {
		console.error("Error fetching linked vehicles:", error);
		return [];
	}
};

/**
 * Fetch equipment linked to a funding source
 */
export const getLinkedEquipment = async (fundingSourceId, userAgency) => {
	try {
		const service = await getService(serviceNames.perEquipment);
		const response = await service.find({
			query: {
				fundingSourceId: fundingSourceId,
				agencyId: userAgency,
				isDeleted: false,
				$limit: -1,
			},
		});
		return response?.data || response || [];
	} catch (error) {
		console.error("Error fetching linked equipment:", error);
		return [];
	}
};

/**
 * Fetch work orders linked to a funding source
 */
export const getLinkedWorkOrders = async (fundingSourceId, userAgency) => {
	try {
		const service = await getService(serviceNames.perWorkOrders);
		const response = await service.find({
			query: {
				fundingSourceId: fundingSourceId,
				agencyId: userAgency,
				isDeleted: false,
				$limit: -1,
				$sort: { openedDate: -1 },
			},
		});
		return response?.data || response || [];
	} catch (error) {
		console.error("Error fetching linked work orders:", error);
		return [];
	}
};

/**
 * Fetch maintenance records linked to a funding source
 */
export const getLinkedMaintenance = async (fundingSourceId, userAgency) => {
	try {
		const service = await getService(serviceNames.perEquipmentMaintenance);
		const response = await service.find({
			query: {
				fundingSourceId: fundingSourceId,
				agencyId: userAgency,
				isDeleted: false,
				$limit: -1,
				$sort: { date: -1 },
			},
		});
		return response?.data || response || [];
	} catch (error) {
		console.error("Error fetching linked maintenance:", error);
		return [];
	}
};

/**
 * Fetch dispensable items linked to a funding source
 */
export const getLinkedDispensableItems = async (
	fundingSourceId,
	userAgency,
) => {
	try {
		const service = await getService(serviceNames.perDispensableItems);
		const response = await service.find({
			query: {
				fundingSourceId: fundingSourceId,
				agencyId: userAgency,
				isDeleted: false,
				$limit: -1,
			},
		});
		return response?.data || response || [];
	} catch (error) {
		console.error("Error fetching linked dispensable items:", error);
		return [];
	}
};

export const getLinkedDispensableIssuances = async (
	fundingSourceId,
	userAgency,
) => {
	try {
		const service = await getService(serviceNames.perDispensableIssuances);
		const response = await service.find({
			query: {
				fundingSourceId: fundingSourceId,
				agencyId: userAgency,
				isDeleted: false,
				isRestock: true, // Only get restocks for funding tracking
				$limit: -1,
				$sort: { date: -1 },
			},
		});
		return response?.data || response || [];
	} catch (error) {
		console.error("Error fetching linked dispensable issuances:", error);
		return [];
	}
};

/**
 * Fetch all linked records for a funding source in parallel
 */
export const getFundingSourceLinkedRecords = async (
	fundingSourceId,
	userAgency,
) => {
	try {
		const [
			vehicles,
			equipment,
			workOrders,
			maintenance,
			dispensableItems,
			dispensableIssuances,
		] = await Promise.all([
			getLinkedVehicles(fundingSourceId, userAgency),
			getLinkedEquipment(fundingSourceId, userAgency),
			getLinkedWorkOrders(fundingSourceId, userAgency),
			getLinkedMaintenance(fundingSourceId, userAgency),
			getLinkedDispensableItems(fundingSourceId, userAgency),
			getLinkedDispensableIssuances(fundingSourceId, userAgency),
		]);

		return {
			vehicles,
			equipment,
			workOrders,
			maintenance,
			dispensableItems,
			dispensableIssuances,
		};
	} catch (error) {
		console.error("Error fetching funding source linked records:", error);
		return {
			vehicles: [],
			equipment: [],
			workOrders: [],
			maintenance: [],
			dispensableItems: [],
			dispensableIssuances: [],
		};
	}
};

/**
 * Get complete funding source details with all linked records
 */
export const getFundingSourceDetails = async (fundingSourceId, userAgency) => {
	try {
		const [fundingSource, linkedRecords] = await Promise.all([
			getFundingSourceData(fundingSourceId),
			getFundingSourceLinkedRecords(fundingSourceId, userAgency),
		]);

		const fsData = fundingSource?.data?.[0] || fundingSource || null;

		return {
			fundingSource: fsData,
			...linkedRecords,
		};
	} catch (error) {
		console.error("Error fetching funding source details:", error);
		return {
			fundingSource: null,
			vehicles: [],
			equipment: [],
			workOrders: [],
			maintenance: [],
			dispensableItems: [],
		};
	}
};
